<template>
  <div class="is-fullhd">
    <notifications group="informationtab" />
    <network-error v-if="hasNetworkError" />
    <div v-else>
      <div
        v-if="hasFetchedStyles"
        id="app"
        class="has-navbar-fixed-top has-text-light"
      >
        <transition
          name="page-change"
          mode="out-in"
        >
          <webcom-authenticator v-if="(!isPublicPage && !user && sessionChecked)" />
          <router-view v-else />
        </transition>
        <transition name="modal-wrapper">
          <container-modal
            v-if="modal"
            :modal-data="modal"
          />
        </transition>
      </div>

      <div
        v-else
        class="pageloader"
      >
        <span class="title">{{/* todo: put loader here */}}</span>
      </div>
    </div>
  </div>
</template>
<script>
import WebcomAuthenticator from '@/views/WebcomAuthenticator'
import NetworkError from '@/views/NetworkError'
import { mapState, mapGetters, mapActions } from 'vuex'
import ContainerModal from '@/components/modals/ContainerModal'
import { getCustomBrandStyles } from '@/services/i18n'
import domainOverviewMixin from '@/mixins/domain-overview'
import constants from '@/helpers/constants.js'
const rootElement = document.documentElement

export default {
  name: 'App',
  components: {
    WebcomAuthenticator,
    ContainerModal,
    NetworkError
  },
  mixins: [domainOverviewMixin],
  data () {
    return {
      modal: null,
      // This property allows us to guard against showing the component
      // until custom styles are given an opportunity to be received.
      hasFetchedStyles: false
    }
  },
  computed: {
    ...mapState(['sessionChecked', 'hasNetworkError', 'useLocalStyle', 'services']),
    ...mapGetters(['clientCredentials', 'brandConfigValue', 'brandConfigSection', 'brandCustomStyles', 'preferredLanguage', 'ssoRedirect', 'user']),

    isPublicPage () {
      if (this.$route.name === 'btTourNewWindow' || this.$route.name === 'ecommTourNewWindow') {
        return true
      }
      return false
    }
  },
  watch: {
    services: function () {
      this.getDomainDetails(this.services.domainregistration)
    },
    preferredLanguage: function () {
      this.initI18nLanguage()
    }
  },
  async created () {
     const { hostname } = window.location
    // const hostname = 'tide-stg.web.com'
    // const hostname = 'fcr-stg.web.com'
    //const hostname = 'bt-stg.web.com'
    // const hostname = '1800accountant-stg.web.com'
    // const hostname = 'webcom-uk-brand'
    // const hostname = 'dev-customer-ui.azurefda.net'

    this.setHostName(hostname)

    // For whitelabel capability. If the hostname is different from those designated
    // as our web.com hosts, we need to send a request for custom styles/logos/background images.
    if (!['localhost', 'webcom-uk-brand'].includes(hostname)) {
      // call function
      const response = await getCustomBrandStyles(hostname, this.$apollo)
      if (response.getBrandByDomainName !== null) {
        // Store result as state un vuex store
        this.setBrandConfig(response.getBrandByDomainName)
      } else {
        this.setBrandConfig(constants.TENANT_STYLES_FALLBACK)
      }
      this.setUseLocalStyle(false)
      this.applyCustomStyles(this.$store.getters.brandCustomStyles)
    } else {
      this.changeFavicon('/favicon.ico')
    }

    this.hasFetchedStyles = true

    // set up the events to show and hide modals. The callback is defined
    // when the modal is shown, and called whent the modal is closed. This is
    // useful for cleanup, such as resetting animations.
    let cb

    // The modal is set which will update this component and result in that modal being shown.
    // Modal is set to default if you forget to include a modal, this will show a modal message
    // reminding you of what you need to do. If a callback is recieved, it is stored in the closure, ready
    // to invocate once the modal is closed.
    this.$root.$on('show::custom::modal', (modal = 'default', callback) => {
      rootElement.classList.add('is-clipped')
      cb = callback
      this.modal = modal
    })

    // When the modal is closed, we set the modal property to null, thus making the
    // modal disappear. We also invocate the callback if one was received as an argument
    // when the modal was opened. Removal of the is-clipped class allows the page to scroll
    // again
    this.$root.$on('close::custom::modal', (modal) => {
      rootElement.classList.remove('is-clipped')
      cb && cb()
      this.modal = null
    })

    // when the app is created, we check if the user has a session in the localstorage
    this.handleSession()
  },
  mounted () {
    // Logic to handle SSO redirect with logged user
    let ssoCall = false

    if (!this.ssoRedirect) {
      try {
        ssoCall = new URLSearchParams(window.location.search).get('sso')
      } catch (err) {
        this.$emit('displayError', 'This browser version is not fully supported, please update to the latest version for the best experience.')
        this.$router.history.push('/')
      }

      if (ssoCall) {
        this.setSSORedirect(ssoCall)
        this.signOut()
      }
    }
  },
  beforeDestroy () {
    this.$root.$off('show::custom::modal')
    this.$root.$off('close::custom::modal')
  },
  methods: {
    ...mapActions(['addLogos', 'addBackground', 'setHostName', 'setBrandConfig', 'setUseLocalStyle', 'initI18nLanguage', 'handleSession', 'setSSORedirect', 'clearSessionData']),
    changeFavicon (link) {
      const favicon = document.querySelector('link[rel="icon"]')
      favicon.href = link
    },

    applyCustomStyles ({ colors = {}, logos = [], favicon, background }) {
      const { style } = document.documentElement

      // graphql won't allow hyphens but css variables need them. So iterate
      // throught the colors and convert them to valid css variables
      Object.keys(colors).forEach(value => {
        if (colors[value] != null) {
          style.setProperty(`--${value.replace(/_/g, '-')}`, colors[value])
        }
      })

      if (logos.length) {
        this.addLogos(logos)
      }

      if (background) {
        this.addBackground(background)
      }

      if (favicon) {
        this.changeFavicon(favicon)
      }
    },
    async signOut () {
      await this.$auth.signOut()
      this.handleSession(null)
      this.$router.history.push('/')
    }
  }
}
</script>
<style>
html {
  overflow-y: auto !important;
}

body {
  margin: 0;
  padding: 0;
}

* {
  box-sizing: border-box;
}

#app {
  height: 100%;
}
</style>
<style lang="scss">
  @import '../sass/_mixins.scss';
  @import '../sass/_animations.scss';
  @import '../sass/mystyles.scss';
  @import '../sass/_variables.scss';
  @import '../sass/_transitions.scss';

  html {
    font-size: $root-font-size;

    body {
      font-size: 1.6em;
    }
  }

</style>
